/*
  Listens for postMessage events from the SkinMatch quiz iframe
  (https://getskinmatch.com) and adds the matched product(s) to the cart.
  The quiz identifies products by EAN/UPC. Resolution is done per-request
  via Shopify's predictive search (variants.barcode field) so this scales
  to any catalog size with no pre-rendered map.
*/
(function () {
  var ALLOWED_ORIGINS = ['https://getskinmatch.com', 'https://www.getskinmatch.com'];
  function getCartAddUrl() {
    var base = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
    return /\.js$/.test(base) ? base : base + '.js';
  }

  function stripLeadingZeros(raw) {
    var s = String(raw || '').replace(/\D/g, '');
    return s.replace(/^0+/, '') || s;
  }

  function parseList(raw) {
    return String(raw || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function parseEanList(raw) { return parseList(raw); }
  function parseSkuList(raw) { return parseList(raw); }

  // SkinMatch is configured so that the SKU field carries the Shopify variant ID.
  // We trust it only if it parses cleanly as a positive integer.
  function skuToVariantId(sku) {
    var s = String(sku || '').trim();
    if (!/^\d{6,}$/.test(s)) return null;
    var n = parseInt(s, 10);
    return n > 0 ? n : null;
  }

  function suggestByBarcode(query) {
    var url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(query)
      + '&resources[type]=product'
      + '&resources[options][fields]=variants.barcode'
      + '&resources[limit]=1';
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return null;
        var list = j.resources && j.resources.results && j.resources.results.products;
        return (list && list[0]) || null;
      });
  }

  function variantIdFromProductUrl(url) {
    var m = /[?&]variant=(\d+)/.exec(url || '');
    return m ? parseInt(m[1], 10) : null;
  }

  // Look up the product JSON and return ONLY the variant whose barcode matches
  // the incoming EAN exactly (with and without leading zeros). Never falls back
  // to an arbitrary variant — if no exact match, we return null and skip the item.
  function variantIdForHandleByBarcode(handle, ean) {
    var targets = [ean, stripLeadingZeros(ean)].filter(function (v, i, a) { return v && a.indexOf(v) === i; });
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants || !p.variants.length) return null;
        var match = p.variants.find(function (v) {
          var bc = String(v.barcode || '').trim();
          if (!bc) return false;
          return targets.indexOf(bc) !== -1 || targets.indexOf(stripLeadingZeros(bc)) !== -1;
        });
        return match ? match.id : null;
      });
  }

  function resolveEanToVariantId(ean) {
    var candidates = [ean, stripLeadingZeros(ean)].filter(function (v, i, a) { return v && a.indexOf(v) === i; });
    var chain = Promise.resolve(null);
    candidates.forEach(function (q) {
      chain = chain.then(function (hit) {
        if (hit) return hit;
        return suggestByBarcode(q);
      });
    });
    return chain.then(function (product) {
      if (!product) return null;
      // Verify variant by barcode (don't trust the URL or "first available" anymore).
      return variantIdForHandleByBarcode(product.handle, ean).then(function (id) {
        if (id) return { id: id, handle: product.handle, title: product.title };
        // Last resort only when product URL contains a specific variant id —
        // the variant id IS the unique answer in that case.
        var variantId = variantIdFromProductUrl(product.url);
        return variantId ? { id: variantId, handle: product.handle, title: product.title } : null;
      });
    }).catch(function () { return null; });
  }

  function getMiniCart() {
    return document.querySelector('mini-cart');
  }

  function getDrawerSections() {
    var miniCart = getMiniCart();
    return miniCart && typeof miniCart.getSectionsToRender === 'function'
      ? miniCart.getSectionsToRender().map(function (s) { return s.id; })
      : [];
  }

  function postCartAdd(body) {
    return fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  // Bulk add — atomic on Shopify's side. If ANY item fails, ALL fail with 422.
  function bulkAddItemsToCart(items) {
    var sections = getDrawerSections();
    var body = { items: items };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    return postCartAdd(body);
  }

  // Per-item fallback — adds each item individually so out-of-stock items
  // (or any other variant-level rejection) only skip themselves, not the whole routine.
  function addItemsIndividually(items) {
    var sections = getDrawerSections();
    var failures = [];
    var lastSuccess = null;

    return items.reduce(function (chain, item) {
      return chain.then(function () {
        return postCartAdd({ items: [item] })
          .then(function (res) { lastSuccess = res; })
          .catch(function (err) {
            console.warn('[SkinMatch] Individual add failed for variant', item.id, err);
            failures.push({ id: item.id, error: err });
          });
      });
    }, Promise.resolve()).then(function () {
      // After all items processed, fetch the cart sections so the drawer reflects state.
      if (!sections.length) {
        if (lastSuccess) lastSuccess._partialFailures = failures;
        return lastSuccess || { _partialFailures: failures };
      }
      var sectionsParam = sections.join(',');
      return fetch('/?sections=' + encodeURIComponent(sectionsParam), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sectionsJson) {
          var result = lastSuccess || {};
          if (sectionsJson) result.sections = sectionsJson;
          result._partialFailures = failures;
          return result;
        })
        .catch(function () {
          if (lastSuccess) lastSuccess._partialFailures = failures;
          return lastSuccess || { _partialFailures: failures };
        });
    });
  }

  // Try bulk first (one fast round-trip); fall back to per-item on 422.
  function addItemsToCart(items) {
    return bulkAddItemsToCart(items).catch(function (err) {
      console.warn('[SkinMatch] Bulk add failed (likely one item unavailable). Falling back to per-item adds.', err);
      return addItemsIndividually(items);
    });
  }

  function refreshCartDrawer(response) {
    // Theme path: rerender sections and open the drawer.
    var miniCart = getMiniCart();
    if (miniCart && response && response.sections && typeof miniCart.renderContents === 'function') {
      miniCart.renderContents(response);
      return true;
    }
    return false;
  }

  function fallbackOpenCartDrawer() {
    // If sections weren't returned (or mini-cart isn't on the page),
    // refetch the cart drawer section HTML and swap it in, then open the drawer.
    var miniCart = getMiniCart();
    if (!miniCart) return Promise.resolve(false);

    return fetch('/?section_id=mini-cart', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return false;
        var container = document.getElementById('mini-cart');
        if (container && typeof miniCart.getSectionInnerHTML === 'function') {
          container.innerHTML = miniCart.getSectionInnerHTML(html, '.shopify-section');
        }
        // Refresh the cart icon bubble too
        return fetch('/?section_id=cart-icon-bubble', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (bubbleHtml) {
            if (bubbleHtml) {
              var bubble = document.getElementById('cart-icon-bubble');
              if (bubble && typeof miniCart.getSectionInnerHTML === 'function') {
                bubble.innerHTML = miniCart.getSectionInnerHTML(bubbleHtml, '.shopify-section');
              }
              var mobileBubble = document.getElementById('mobile-cart-icon-bubble');
              if (mobileBubble && typeof miniCart.getSectionInnerHTML === 'function') {
                mobileBubble.innerHTML = miniCart.getSectionInnerHTML(bubbleHtml, '.shopify-section');
              }
            }
            if (typeof miniCart.open === 'function') miniCart.open();
            return true;
          });
      })
      .catch(function () { return false; });
  }

  function notifyThemeCart(response) {
    // Mirror the theme's own product-added event so analytics / observers fire.
    // Intentionally DO NOT dispatch `cart:refresh` here — cart-options.js listens
    // for that and runs its gift-box reconciliation, which can reload the page.
    // The drawer + cart icon are already refreshed via miniCart.renderContents().
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      bubbles: true,
      detail: { product: response }
    }));
  }

  function handleAddToCart(data) {
    // SkinMatch may send `sku` (Shopify variant ID) and/or `ean` as comma-separated lists.
    // Prefer SKU (deterministic — no fuzzy lookup); fall back to EAN per item.
    var skus = parseSkuList(data.sku);
    var eans = parseEanList(data.ean);

    // Pair sku/ean by index so a missing SKU at position N can fall back to EAN at position N.
    var pairCount = Math.max(skus.length, eans.length);
    if (!pairCount) return;

    var pairs = [];
    for (var i = 0; i < pairCount; i++) {
      pairs.push({ sku: skus[i], ean: eans[i] });
    }

    Promise.all(pairs.map(function (p) {
      var directId = skuToVariantId(p.sku);
      if (directId) return Promise.resolve({ ean: p.ean, sku: p.sku, hit: { id: directId, source: 'sku' } });
      if (!p.ean) return Promise.resolve({ ean: p.ean, sku: p.sku, hit: null });
      return resolveEanToVariantId(p.ean).then(function (hit) {
        if (hit) hit.source = 'ean';
        return { ean: p.ean, sku: p.sku, hit: hit };
      });
    })).then(function (results) {
      var items = [];
      var missing = [];
      var resolutions = [];
      results.forEach(function (r) {
        if (r.hit) {
          items.push({ id: r.hit.id, quantity: 1 });
          resolutions.push({
            variantId: r.hit.id,
            resolvedVia: r.hit.source, // 'sku' = direct, deterministic; 'ean' = barcode lookup
            sku: r.sku || null,
            ean: r.ean || null,
            handle: r.hit.handle || null,
            title: r.hit.title || null
          });
        } else {
          missing.push({ ean: r.ean, sku: r.sku });
        }
      });

      console.log('[SkinMatch] Resolved variants:', resolutions);
      var skuCount = resolutions.filter(function (x) { return x.resolvedVia === 'sku'; }).length;
      var eanCount = resolutions.filter(function (x) { return x.resolvedVia === 'ean'; }).length;
      console.log('[SkinMatch] Resolution summary — direct SKU/variant ID:', skuCount, '· barcode (EAN) lookup:', eanCount);

      if (missing.length) {
        console.warn('[SkinMatch] No Shopify variant found for:', missing);
      }
      if (!items.length) return;

      console.log('[SkinMatch] POST /cart/add.js items:', items);

      return addItemsToCart(items).then(function (res) {
        var partialFailures = (res && res._partialFailures) || [];
        if (partialFailures.length) {
          var totalRequested = items.length;
          var addedCount = totalRequested - partialFailures.length;
          console.warn('[SkinMatch] Partial add — added', addedCount, 'of', totalRequested, '· failed:', partialFailures);
          notifyUser(addedCount, totalRequested, partialFailures);
        }
        var refreshed = refreshCartDrawer(res);
        notifyThemeCart(res);
        console.log('[SkinMatch] Added to cart:', res);
        if (!refreshed) {
          // Server didn't return sections (or mini-cart isn't ready) — refetch and open.
          return fallbackOpenCartDrawer();
        }
      });
    }).catch(function (err) {
      console.error('[SkinMatch] Cart add failed:', err);
      notifyUser(0, 0, [], err && err.description ? err.description : 'Could not add products to cart. Please try again.');
    });
  }

  // Show a brief toast to the user when a routine add partially or fully fails.
  // We don't gate on whether the iframe is visible — the toast attaches to <body>.
  function notifyUser(addedCount, totalRequested, partialFailures, errorMessage) {
    var msg;
    if (errorMessage) {
      msg = errorMessage;
    } else if (addedCount === 0) {
      msg = 'No products were added to your cart. Please try again.';
    } else if (partialFailures.length) {
      msg = 'Added ' + addedCount + ' of ' + totalRequested + ' products. ' +
            partialFailures.length + ' could not be added (some may be out of stock).';
    } else {
      return;
    }
    showToast(msg);
  }

  function showToast(message) {
    var toast = document.createElement('div');
    toast.className = 'skinmatch-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'status');
    toast.style.cssText = [
      'position:fixed', 'top:20px', 'left:50%', 'transform:translateX(-50%)',
      'background:#1a1b18', 'color:#fff', 'padding:14px 22px', 'border-radius:4px',
      'font-size:14px', 'font-family:inherit', 'z-index:99999',
      'box-shadow:0 4px 20px rgba(0,0,0,0.15)', 'max-width:90vw', 'text-align:center',
      'opacity:0', 'transition:opacity 0.25s ease'
    ].join(';');
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.style.opacity = '1'; });
    setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 5000);
  }

  window.addEventListener('message', function (e) {
    if (ALLOWED_ORIGINS.indexOf(e.origin) === -1) return;
    var d = e.data;
    // TEMP DIAGNOSTIC: log every message from the iframe so we can see if
    // "Add Routine to Cart" is firing add-to-cart (and with what payload).
    console.log('[SkinMatch] postMessage received:', d);
    if (!d || typeof d !== 'object') return;
    if (d.action === 'add-to-cart') handleAddToCart(d);
  });
})();